'use client';

import { AlertTriangle, Home } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { useSubscriptionFeatureAccess } from '@/hooks/use-subscription-feature-access';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { FeatureName } from '@/lib/feature-access';
import { paths } from '@/constants/paths';
import { hasPricingPage } from '@/constants/pricing';

import { appConfig } from '@/config/app';

const featureRouteMap: Record<string, FeatureName> = {
  '/keywords': 'keywords',
  '/content-assistant': 'content-assistant',
  '/gsc': 'gsc',
  '/rank-tracker': 'rank-tracker',
  '/backlinks': 'backlinks',
  '/site-stats': 'site-stats',
  '/site-audit': 'site-audit',
  '/geogrid': 'geogrid',
};

const featureConfig: Record<
  FeatureName,
  { title: string; description: string }
> = {
  'rank-tracker': {
    title: 'Rank Tracker Subscription Expired',
    description:
      'Your Rank Tracker subscription has expired. Upgrade to continue tracking keyword rankings and access all features.',
  },
  geogrid: {
    title: 'GeoGrid Subscription Expired',
    description:
      'Your GeoGrid subscription has expired. Upgrade to continue tracking local rankings across different locations.',
  },
  keywords: {
    title: 'Keyword Research Subscription Expired',
    description:
      'Your Keyword Research subscription has expired. Upgrade to continue researching keywords and accessing advanced tools.',
  },
  'content-assistant': {
    title: 'Content Assistant Subscription Expired',
    description:
      'Your Content Assistant subscription has expired. Upgrade to continue generating high-quality content.',
  },
  backlinks: {
    title: 'Backlinks Subscription Expired',
    description:
      'Your Backlinks subscription has expired. Upgrade to continue analyzing backlink profiles and competitor data.',
  },
  gsc: {
    title: 'Search Console Subscription Expired',
    description:
      'Your Search Console subscription has expired. Upgrade to continue analyzing search performance data.',
  },
  'site-stats': {
    title: 'Site Stats Subscription Expired',
    description:
      'Your Site Stats subscription has expired. Upgrade to continue monitoring website performance metrics.',
  },
  'site-audit': {
    title: 'Site Audit Subscription Expired',
    description:
      'Your Site Audit subscription has expired. Upgrade to continue performing comprehensive site audits.',
  },
};

export function SubscriptionExpiredPopup() {
  const pathname = usePathname();
  const { data: featureAccess, isLoading } = useSubscriptionFeatureAccess();
  const [showPopup, setShowPopup] = useState(false);
  const [currentFeature, setCurrentFeature] = useState<FeatureName | null>(
    null,
  );

  useEffect(() => {
    if (isLoading || !featureAccess) return;

    // Find the current feature based on pathname
    const currentFeatureName = Object.entries(featureRouteMap).find(([route]) =>
      pathname.startsWith(route),
    )?.[1] as FeatureName | undefined;

    if (!currentFeatureName) {
      setShowPopup(false);
      return;
    }

    const access = featureAccess[currentFeatureName];

    // Show popup if user has "view" access (had subscription but it's expired)
    if (access?.accessLevel === 'view') {
      setCurrentFeature(currentFeatureName);
      setShowPopup(true);
    } else {
      setShowPopup(false);
    }
  }, [pathname, featureAccess, isLoading]);

  if (!showPopup || !currentFeature) {
    return null;
  }

  const config = featureConfig[currentFeature];
  const hasPricing = hasPricingPage.includes(currentFeature);

  return (
    <Dialog open={showPopup} onOpenChange={() => {}}>
      <DialogContent
        className='sm:max-w-md'
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className='flex items-center gap-2'>
            <AlertTriangle className='h-5 w-5 text-amber-500' />
            <DialogTitle className='text-lg font-semibold'>
              {config.title}
            </DialogTitle>
          </div>
          <DialogDescription className='text-sm text-muted-foreground'>
            {config.description}
          </DialogDescription>
        </DialogHeader>

        <div className='flex flex-col gap-3 pt-2'>
          <div className='flex gap-2'>
            <Link href={paths.dashboard.index} className='flex-1'>
              <Button variant='outline' className='w-full'>
                <Home className='mr-2 h-4 w-4' />
                Back to Dashboard
              </Button>
            </Link>

            {hasPricing ? (
              <Link
                href={paths.pricing.feature(currentFeature)}
                className='flex-1'
              >
                <Button className='w-full'>View Pricing & Upgrade</Button>
              </Link>
            ) : (
              <Link
                href={`mailto:${appConfig.email.support}?subject=Subscription Inquiry for ${currentFeature}`}
                className='flex-1'
                target='_blank'
              >
                <Button className='w-full'>Contact Sales</Button>
              </Link>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
